import { DependencyList, useCallback, useEffect, useState } from 'react'

import { Direction } from './SnakeGame'

// minimum touch movement diff (in px) needed to consider it
// an actual movement and not just a tap
const TOUCH_MIN_RANGE = 10

function useTouchDirection(
  onDirectionInput: (direction: Direction) => void,
  dependencies: DependencyList,
) {
  const [startingX, setStartingX] = useState(0)
  const [startingY, setStartingY] = useState(0)

  const handleTouchStart = useCallback((event: TouchEvent) => {
    // Save starting position
    // eslint-disable-next-line prefer-destructuring
    const touch = event.touches[0]
    setStartingX(touch.clientX)
    setStartingY(touch.clientY)
  }, [])

  const handleTouchEnd = useCallback(
    (event: TouchEvent) => {
      // Calculate difference between starting and ending positions
      // eslint-disable-next-line prefer-destructuring
      const touch = event.changedTouches[0]
      const endingX = touch.clientX
      const endingY = touch.clientY
      const diffX = endingX - startingX
      const diffY = endingY - startingY

      // Determine swipe direction
      if (Math.abs(diffX) > Math.abs(diffY)) {
        // Horizontal swipe
        if (diffX > TOUCH_MIN_RANGE) {
          onDirectionInput('right')
        } else if (diffX < -TOUCH_MIN_RANGE) {
          onDirectionInput('left')
        }
      } else {
        // Vertical swipe
        if (diffY > TOUCH_MIN_RANGE) {
          onDirectionInput('down')
        } else if (diffY < -TOUCH_MIN_RANGE) {
          onDirectionInput('up')
        }
      }
    },
    [onDirectionInput, startingX, startingY],
  )

  const dependenciesJson = JSON.stringify(dependencies)

  useEffect(() => {
    document.addEventListener('touchstart', handleTouchStart)
    document.addEventListener('touchend', handleTouchEnd)
    return () => {
      document.removeEventListener('touchstart', handleTouchStart)
      document.removeEventListener('touchend', handleTouchEnd)
    }
  }, [handleTouchStart, handleTouchEnd, dependenciesJson])
}

function useKeyboardDirection(
  onDirectionInput: (direction: Direction) => void,
  dependencies: DependencyList,
) {
  const dependenciesJson = JSON.stringify(dependencies)
  useEffect(() => {
    function keyHandler(event: KeyboardEvent) {
      const { key } = event
      if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) {
        // not arrow key, ignore
        return
      }

      switch (event.key) {
        case 'ArrowUp': {
          onDirectionInput('up')
          break
        }
        case 'ArrowDown': {
          onDirectionInput('down')
          break
        }
        case 'ArrowLeft': {
          onDirectionInput('left')
          break
        }
        case 'ArrowRight': {
          onDirectionInput('right')
          break
        }
      }
    }

    document.addEventListener('keydown', keyHandler)

    return () => document.removeEventListener('keydown', keyHandler)
  }, [onDirectionInput, dependenciesJson])
}

export function useJoystick(
  handler: (direction: Direction) => void,
  dependecies: DependencyList,
): void {
  useTouchDirection(handler, dependecies)
  useKeyboardDirection(handler, dependecies)
}
